import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { AbstractService } from "app/services/abstract.service";
import { environment as env } from "../../../../../../environments/environment.prod";
import { IPagination, IPaginationResultFrequencies } from "app/common/interfaces";

import { OrderAttributes } from "@albatrosdeveloper/ave-models-package";
import { IUpdateOrder } from "./interfaces/order.interface";
import { TripLoadResponse, TripMaterialsResponse } from './interfaces/trip-load-response.interface';

const apiUrl = env.API_AVE_ORDER_URL;
const loadUrl = env.API_AVE_LOAD_PREPARATION;
const moduleName = "order";

@Injectable({
  providedIn: "root",
})
export class LoadsService extends AbstractService<any, any, any> {
  currentFilter: CombinedFilter<any>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  getOrder(
    serial: string,
    ): Observable<IPaginationResultFrequencies<OrderAttributes>> {
  
    const query = buildQuery<OrderAttributes>(
      andWhere("serial", Ops.eq(serial)),
      select(["_id","serial","type", "detail"]) // agregugue type
    );

    const API_URL = `${apiUrl}api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResultFrequencies<OrderAttributes>>(API_URL);
  }

  findUnattendedOrders(
    typeCode: string = 'DT003',
    date: {start: string, end: string},
    companyId: number,
    salesOfficeGroup?: string,
    salesOffice: number[] = [],
    route?: string,
    routeArea?: string,
    routeGroup?: string,
    subRoutes: number[] = [],
    businessPartners: number[] = [],
    documentClassId?: string[],
    options?: IPagination<OrderAttributes>
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {

    if (!date.start || !date.end) {
      throw new Error("startDate y endDate son requeridos.");
    }
  
    if (!companyId) {
      throw new Error("companyId es requerido.");
    }

    const queryParts: any[] = [
      where("serial" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("trip.id", Ops.like(options.search ?? "", "i")),
      andAllWhere("type.code", Ops.eq(typeCode)),
      andAllWhere("company.id", Ops.eq(companyId)),
      andAllWhere("loadDateTime", Ops.btwnDates(date.start, date.end)),
      andAllWhere("trip.dateHourEnd", Ops.not(null)),
      andAllWhere("operationType", Ops.not(4)), //4->documento de carga unificado
      andAllWhere("statusId", Ops.eq(1)), //0-> inactivo, 8-> rechazado, 9->entregado, 10->parcial entregado, 11->rechazado por cliente
      //andAllWhere("statusERP", Ops.eq(0)),
    ];
    
    if (documentClassId && documentClassId.length > 0) {
      queryParts.push(andAllWhere("class.code", Ops.in(...documentClassId)));
    }

    if (salesOffice && salesOffice.length > 0) {
      queryParts.push(andAllWhere("salesOffice.id", Ops.in(...salesOffice)));
    }
  
    if (route){
      queryParts.push(andAllWhere ("routes.id", Ops.in(...[route])));
    }

    if (subRoutes && subRoutes.length > 0) {
      queryParts.push(andAllWhere("subRoute.id", Ops.in(...subRoutes)));
    }else{
      queryParts.push(andAllWhere("subRoute", Ops.not(null)));
    }

    if(businessPartners && businessPartners.length > 0){
      queryParts.push(andAllWhere("shipTo.id", Ops.in(...businessPartners)));
    }
  
    const query = buildQuery<any>(
      ...queryParts,
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
  
    let queryParams = '';
  
    if (salesOfficeGroup) {
      queryParams += `&salesOfficeGroupId=${salesOfficeGroup}`;
    }
  
    if(routeArea){
      queryParams += `routeAreaId=${routeArea}`;
    }

    if (routeGroup) {
      queryParams += `&routeGroupId=${routeGroup}`;
    }
    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}${queryParams}`;
    return this.http.get<IPaginationResultFrequencies<OrderAttributes>>(API_URL);
  }

  findOrdersWithoutRoute(
    typeCode: string = 'DT003',
    companyId: number,
    salesOfficeGroup?: string,
    salesOffice: number[] = [],
    route?: string,
    routeArea?: string,
    routeGroup?: string,
    subRoutes?: number[],
    businessPartners: number[] = [],
    documentClassId?: string[],
    options?: IPagination<OrderAttributes>
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {

    if (!companyId) {
      throw new Error("companyId es requerido.");
    }    
  
    const queryParts: any[] = [
      where("serial" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      andAllWhere("type.code", Ops.eq(typeCode)),
      andAllWhere("company.id", Ops.eq(companyId)),
      andAllWhere("subRoute", Ops.eq(null)),
      andAllWhere("statusId", Ops.eq(1)),
      //andAllWhere("statusERP", Ops.eq(0)),
    ];

    if (documentClassId && documentClassId.length > 0) {
      queryParts.push(andAllWhere("class.code", Ops.in(...documentClassId)));
    }
  
    if (salesOffice && salesOffice.length > 0) {
      queryParts.push(andAllWhere("salesOffice.id", Ops.in(...salesOffice)));
    }

    if (route){
      queryParts.push(andAllWhere("routes.id", Ops.in(...[route])));
    }

    if (subRoutes && subRoutes.length > 0) {
      queryParts.push(andAllWhere("subRoute.id", Ops.in(...subRoutes)));
    }else{
      queryParts.push(andAllWhere("subRoute", Ops.not(null)));
    }
  
    if(businessPartners && businessPartners.length > 0){
      queryParts.push(andAllWhere("shipTo.id", Ops.in(...businessPartners)));
    }

    const query = buildQuery<any>(
      ...queryParts,
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
  
    let queryParams = '';
  
    if (salesOfficeGroup) {
      queryParams += `&salesOfficeGroupId=${salesOfficeGroup}`;
    }
  
    if(routeArea){
      queryParams += `routeAreaId=${routeArea}`;
    }

    if (routeGroup) {
      queryParams += `&routeGroupId=${routeGroup}`;
    }

    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}${queryParams}`;
    return this.http.get<IPaginationResultFrequencies<OrderAttributes>>(API_URL);
  }

  findOrdersWithoutDeliveryDate(
    typeCode: string = 'DT003',
    companyId: number,
    salesOfficeGroup?: string,
    salesOffice: number[] = [],
    route?: string,
    routeArea?: string,
    routeGroup?: string,
    subRoutes: number[] = [],
    businessPartners: number[] = [],
    documentClassId?: string[],
    options?: IPagination<OrderAttributes>
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {
  
    if (!companyId) {
      throw new Error("companyId es requerido.");
    }
  
    const queryParts: any[] = [
      where("serial" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      andAllWhere("type.code", Ops.eq(typeCode)),
      andAllWhere("company.id", Ops.eq(companyId)),
      andAllWhere("deliveryDateTime", Ops.eq(null)),
      andAllWhere("statusId", Ops.eq(1)),
      //andAllWhere("statusERP", Ops.eq(0)),

    ];
  
    if (salesOffice && salesOffice.length > 0) {
      queryParts.push(andAllWhere("salesOffice.id", Ops.in(...salesOffice)));
    }

    if (documentClassId && documentClassId.length > 0) {
      queryParts.push(andAllWhere("class.code", Ops.in(...documentClassId)));
    }
  
    if (route){
      queryParts.push(andAllWhere("routes.id", Ops.in(...[route])));
    }

    if (subRoutes && subRoutes.length > 0) {
      queryParts.push(andAllWhere("subRoute.id", Ops.in(...subRoutes)));
    }else{
      queryParts.push(andAllWhere("subRoute", Ops.not(null)));
    }

    if(businessPartners && businessPartners.length > 0){
      queryParts.push(andAllWhere("shipTo.id", Ops.in(...businessPartners)));
    }
  
    const query = buildQuery<any>(
      ...queryParts,
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
  
    let queryParams = '';
  
    if (salesOfficeGroup) {
      queryParams += `&salesOfficeGroupId=${salesOfficeGroup}`;
    }
  
    if(routeArea){
      queryParams += `routeAreaId=${routeArea}`;
    }

    if (routeGroup) {
      queryParams += `&routeGroupId=${routeGroup}`;
    }
    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}${queryParams}`;
    return this.http.get<IPaginationResultFrequencies<OrderAttributes>>(API_URL);
  }

  findOrdersWithoutLoadDate(
    typeCode: string = 'DT003',
    companyId: number,
    salesOfficeGroup?: string,
    salesOffice: number[] = [],
    route?: string,
    routeArea?: string,
    routeGroup?: string,
    businessPartners: number[] = [],
    options?: IPagination<OrderAttributes>
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {

    if (!companyId) {
      throw new Error("companyId es requerido.");
    }    
  
    const queryParts: any[] = [
      where("serial" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      andAllWhere("type.code", Ops.eq(typeCode)),
      andAllWhere("company.id", Ops.eq(companyId)),
      andAllWhere("loadDateTime", Ops.eq(null)),
      andAllWhere("statusId", Ops.eq(1)),
      //andAllWhere("statusERP", Ops.eq(0)),
    ];
  
    if (salesOffice && salesOffice.length > 0) {
      queryParts.push(andAllWhere("salesOffice.id", Ops.in(...salesOffice)));
    }

    if (route){
      queryParts.push(andAllWhere("routes.id", Ops.in(...[route])));
    }
  
    if(businessPartners && businessPartners.length > 0){
      queryParts.push(andAllWhere("shipTo.id", Ops.in(...businessPartners)));
    }

    const query = buildQuery<any>(
      ...queryParts,
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
  
    let queryParams = '';
  
    if (salesOfficeGroup) {
      queryParams += `&salesOfficeGroupId=${salesOfficeGroup}`;
    }
  
    if(routeArea){
      queryParams += `routeAreaId=${routeArea}`;
    }

    if (routeGroup) {
      queryParams += `&routeGroupId=${routeGroup}`;
    }

    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}${queryParams}`;
    
    return this.http.get<IPaginationResultFrequencies<OrderAttributes>>(API_URL);
  }

  findOrdersWithoutTrip(
    typeCode: string = 'DT003',
    date: {start: string, end: string},
    companyId: number,
    salesOfficeGroup?: string,
    salesOffice: number[] = [],
    route?: string,
    routeArea?: string,
    routeGroup?: string,
    subRoutes: number[] = [],
    businessPartners: number[] = [],
    documentClassId?: string[],
    options?: IPagination<OrderAttributes>
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {

    if (!date.start || !date.end) {
      throw new Error("startDate y endDate son requeridos.");
    }
  
    if (!companyId) {
      throw new Error("companyId es requerido.");
    }

    const queryParts: any[] = [
      where("serial" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      andAllWhere("type.code", Ops.eq(typeCode)),
      andAllWhere("company.id", Ops.eq(companyId)),
      //andAllWhere("loadDateTime", Ops.btwnDates(date.start, date.end)),
      //andAllWhere("deliveryDateTime", Ops.not(null)),
      andAllWhere("trip", Ops.eq(null)),
      andAllWhere("statusId", Ops.eq(1)),
      //andAllWhere("statusERP", Ops.eq(0)),

    ];

    if (documentClassId && documentClassId.length > 0) {
      queryParts.push(andAllWhere("class.code", Ops.in(...documentClassId)));
    }
  
    if (salesOffice && salesOffice.length > 0) {
      queryParts.push(andAllWhere("salesOffice.id", Ops.in(...salesOffice)));
    }
  
    if (route){
      queryParts.push(andAllWhere("routes.id", Ops.in(...[route])));
    }

    if (subRoutes && subRoutes.length > 0) {
      queryParts.push(andAllWhere("subRoute.id", Ops.in(...subRoutes)));
    }else{
      // queryParts.push(andAllWhere("subRoute", Ops.not(null)));
    }
  
    if(businessPartners && businessPartners.length > 0){
      queryParts.push(andAllWhere("shipTo.id", Ops.in(...businessPartners)));
    }

    const query = buildQuery<any>(
      ...queryParts,
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
  
    let queryParams = '';
  
    if (salesOfficeGroup) {
      queryParams += `&salesOfficeGroupId=${salesOfficeGroup}`;
    }
  
    if(routeArea){
      queryParams += `routeAreaId=${routeArea}`;
    }

    if (routeGroup) {
      queryParams += `&routeGroupId=${routeGroup}`;
    }
    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}${queryParams}`;
    return this.http.get<IPaginationResultFrequencies<OrderAttributes>>(API_URL);
  }

  findOrdersWithRouteAndTrip(
    typeCode: string = 'DT003',
    date: {start: string, end: string},
    companyId: number,
    salesOfficeGroup?: string,
    salesOffice: number[] = [],
    route?: string,
    routeArea?: string,
    routeGroup?: string,
    subRoutes: number[] = [],
    businessPartners: number[] = [],
    documentClassId?: string[],
    options?: IPagination<OrderAttributes>
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {

    if (!date.start || !date.end) {
      throw new Error("startDate y endDate son requeridos.");
    }
  
    if (!companyId) {
      throw new Error("companyId es requerido.");
    }

    const queryParts: any[] = [
      where("serial" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      andAllWhere("type.code", Ops.eq(typeCode)),
      andAllWhere("loadDateTime", Ops.btwnDates(date.start, date.end)),
      //andAllWhere("deliveryDateTime", Ops.not(null)),
      andAllWhere("trip", Ops.not(null)),
      andAllWhere("statusId", Ops.eq(1)),
      //andAllWhere("statusERP", Ops.eq(0)),

    ];

    if (documentClassId && documentClassId.length > 0) {
      queryParts.push(andAllWhere("class.code", Ops.in(...documentClassId)));
    }
  
    if (salesOffice && salesOffice.length > 0) {
      queryParts.push(andAllWhere("salesOffice.id", Ops.in(...salesOffice)));
    }

    if (route){
      queryParts.push(andAllWhere("routes.id", Ops.in(...[route])));
    }
  
    if (subRoutes && subRoutes.length > 0) {
      queryParts.push(andAllWhere("subRoute.id", Ops.in(...subRoutes)));
    }else{
      queryParts.push(andAllWhere("subRoute", Ops.not(null)));
    }
  
    if(businessPartners && businessPartners.length > 0){
      queryParts.push(andAllWhere("shipTo.id", Ops.in(...businessPartners)));
    }

    const query = buildQuery<any>(
      ...queryParts,
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
  
    let queryParams = '';
  
    if (salesOfficeGroup) {
      queryParams += `&salesOfficeGroupId=${salesOfficeGroup}`;
    }
  
    if(routeArea){
      queryParams += `routeAreaId=${routeArea}`;
    }

    if (routeGroup) {
      queryParams += `&routeGroupId=${routeGroup}`;
    }
    const API_URL = `${loadUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}${queryParams}`;
    return this.http.get<IPaginationResultFrequencies<OrderAttributes>>(API_URL);
  }

/**
 * Obtiene viajes por documentos de carga con paginación y filtros.
 *
 * Estrategia de búsqueda:
 * - Sin búsqueda: Un solo objeto con filtros base
 * - Con búsqueda: Cinco objetos (OR lógico) - busca en 'serial', 'shipTo.externalKeys.code', 
 *   'shipTo.name', 'soldTo.externalKeys.code', 'soldTo.name'
 *
 * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
 *
 * @param typeCode Código de tipo de documento (default: "DT003")
 * @param date Rango de fechas {start, end}
 * @param companyId ID de la compañía (requerido)
 * @param salesOfficeGroup Grupo de oficina de ventas
 * @param salesOffice Array de IDs de oficinas de ventas
 * @param route Ruta
 * @param routeArea Área de ruta
 * @param routeGroup Grupo de ruta
 * @param subRoutes Array de IDs de sub-rutas
 * @param businessPartners Array de IDs de socios comerciales
 * @param documentClassId Array de códigos de clase de documento
 * @param options Opciones de paginación
 * @returns Observable con resultados paginados
 *
 * Search fields: serial, shipTo.externalKeys.code, shipTo.name, soldTo.externalKeys.code, soldTo.name
 * Array filters: salesOffice, subRoutes, businessPartners, documentClassId
 * Single filters: typeCode, route
 * Query params: salesOfficeGroup, routeArea, routeGroup, companyId
 */
getTripsByLoadDocs(
  typeCode: string = "DT003",
  date: { start: string; end: string },
  companyId: number,
  salesOfficeGroup?: string,
  salesOffice: number[] = [],
  route?: string,
  routeArea?: string,
  routeGroup?: string,
  subRoutes: number[] = [],
  businessPartners: number[] = [],
  documentClassId?: string[],
  options?: IPagination<OrderAttributes>
): Observable<TripLoadResponse> {

  // ========== VALIDACIONES ==========
  if (!date.start || !date.end) {
    throw new Error("startDate y endDate son requeridos.");
  }

  if (!companyId) {
    throw new Error("companyId es requerido.");
  }

  const hasSearch = !!options?.search;

  /**
   * Construye las condiciones base que SIEMPRE deben aplicarse.
   * Incluye: typeCode, rango de fechas, estados, y filtros condicionales.
   *
   * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
   */
  const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<OrderAttributes>[] => {
    const initialFilter: FilterFn<OrderAttributes> = params.useOrWhere
      ? orWhere("type.code" as keyof OrderAttributes, Ops.eq(typeCode))
      : where("type.code" as keyof OrderAttributes, Ops.eq(typeCode));

    return [
      // Filtro inicial (where u orWhere según corresponda)
      initialFilter,

      // Filtros de fecha y estado (siempre aplican)
      andWhere("loadDateTime" as keyof OrderAttributes, Ops.btwnDates(date.start, date.end)),
      andWhere("deliveryDateTime" as keyof OrderAttributes, Ops.not(null)),
      andWhere("trip" as keyof OrderAttributes, Ops.not(null)),
      andWhere("statusId" as keyof OrderAttributes, Ops.eq(1)),

      // Filtros condicionales de arrays
      documentClassId && documentClassId.length > 0
        ? andWhere("class.code" as keyof OrderAttributes, Ops.in(...documentClassId))
        : noop(),

      salesOffice && salesOffice.length > 0
        ? andWhere("salesOffice.id" as keyof OrderAttributes, Ops.in(...salesOffice))
        : noop(),

      route
        ? andWhere("routes.id" as keyof OrderAttributes, Ops.in(...[route]))
        : noop(),

      // SubRoutes: si hay array usa in(), si no, valida que no sea null
      subRoutes && subRoutes.length > 0
        ? andWhere("subRoute.id" as keyof OrderAttributes, Ops.in(...subRoutes))
        : andWhere("subRoute" as keyof OrderAttributes, Ops.not(null)),

      businessPartners && businessPartners.length > 0
        ? andWhere("shipTo.id" as keyof OrderAttributes, Ops.in(...businessPartners))
        : noop(),
    ];
  };

  // ========== CONSTRUCCIÓN DE OBJETOS DE BÚSQUEDA ==========

  // Objeto 1: Condiciones base + búsqueda por SERIAL (o solo condiciones si no hay búsqueda)
  const searchBySerialQuery: FilterFn<OrderAttributes>[] = [
    ...buildBaseFilters({ useOrWhere: false }),
    hasSearch ? andWhere("serial" as keyof OrderAttributes, Ops.like(options!.search!, "i")) : noop()
  ];

  // Objeto 2: Condiciones base + búsqueda por SHIP_TO CODE
  const searchByShipToCodeQuery: FilterFn<OrderAttributes>[] = hasSearch ? [
    ...buildBaseFilters({ useOrWhere: true }),
    andWhere("shipTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options!.search!, "i"))
  ] : [];

  // Objeto 3: Condiciones base + búsqueda por SHIP_TO NAME
  const searchByShipToNameQuery: FilterFn<OrderAttributes>[] = hasSearch ? [
    ...buildBaseFilters({ useOrWhere: true }),
    andWhere("shipTo.name" as keyof OrderAttributes, Ops.like(options!.search!, "i"))
  ] : [];

  // Objeto 4: Condiciones base + búsqueda por SOLD_TO CODE
  const searchBySoldToCodeQuery: FilterFn<OrderAttributes>[] = hasSearch ? [
    ...buildBaseFilters({ useOrWhere: true }),
    andWhere("soldTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options!.search!, "i"))
  ] : [];

  // Objeto 5: Condiciones base + búsqueda por SOLD_TO NAME
  const searchBySoldToNameQuery: FilterFn<OrderAttributes>[] = hasSearch ? [
    ...buildBaseFilters({ useOrWhere: true }),
    andWhere("soldTo.name" as keyof OrderAttributes, Ops.like(options!.search!, "i"))
  ] : [];

  // ========== ENSAMBLAJE DE QUERY ==========

  const query = buildQuery<OrderAttributes>(
    ...searchBySerialQuery,
    ...searchByShipToCodeQuery,
    ...searchByShipToNameQuery,
    ...searchBySoldToCodeQuery,
    ...searchBySoldToNameQuery,

    // Ordenamiento y paginación
    orderBy(
      options?.sort ?? "creationDateTime",
      options?.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
    ),
    page(options?.currentPage ?? 1, options?.limitPerPage ?? 100)
  );

  // ========== CONSTRUCCIÓN DE QUERY PARAMS ==========

  const queryParams = new URLSearchParams();

  // Objeto con parámetros opcionales
  const optionalParams: Record<string, string | number | undefined> = {
    salesOfficeGroupId: salesOfficeGroup,
    routeAreaId: routeArea,
    routeGroupId: routeGroup,
    companyId: companyId
  };

  // Agregar solo los parámetros que tienen valor
  Object.entries(optionalParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });

  // ========== URL FINAL ==========

  const queryParamsString = queryParams.toString();
  const API_URL = `${loadUrl}api/${moduleName}/getTripsByLoadDocs?filter=${encodeURIComponent(JSON.stringify(query))}${queryParamsString ? `&${queryParamsString}` : ''}`;

  return this.http.get<TripLoadResponse>(API_URL);
}

getMaterialsByTrip(
  tripMongoId: string,
  filter?: any
): Observable<TripMaterialsResponse> {
  const queryParams = new URLSearchParams();
  if (filter && Object.keys(filter).length > 0) {
    queryParams.append('filter', JSON.stringify(filter));
  }

  const queryString = queryParams.toString();
  const API_URL = `${loadUrl}api/${moduleName}/getMaterialsByTrip/${tripMongoId}${queryString ? `?${queryString}` : ''}`;

  return this.http.get<TripMaterialsResponse>(API_URL);
}



  updateOrder(dataToUpdate: IUpdateOrder): Observable<{ data: IUpdateOrder }> {
    let API_URL: string = `${loadUrl}api/order/`;
    return this.http.patch<{ data: IUpdateOrder }>(API_URL, dataToUpdate);
  }

  joinOrder(dataToJoin): Observable<{ data }> {
    let API_URL: string = `${loadUrl}api/order/join`;
    return this.http.post<{ data }>(API_URL, dataToJoin);
  }

  //order future
  findOrdersFuture(
    typeCode: string = 'DT003',
    date: {start: string, end: string},
    companyId: number,
    salesOfficeGroup?: string,
    salesOffice: number[] = [],
    route?: string,
    routeArea?: string,
    routeGroup?: string,
    subRoutes: number[] = [],
    businessPartners: number[] = [],
    documentClassId?: string[],
    options?: IPagination<OrderAttributes>
  ): Observable<any> { //no tiene attributes

    if (!date.start || !date.end) {
      throw new Error("startDate y endDate son requeridos.");
    }
  
    if (!companyId) {
      throw new Error("companyId es requerido.");
    }
    
    const searchValue = options?.search ?? "";

    const queryParts: any[] = [
      where("serial" as keyof OrderAttributes, Ops.like(searchValue, "i")),
      orWhere("shipTo.externalKeys.code" as keyof OrderAttributes, Ops.like(searchValue ?? "", "i")),
      orWhere("shipTo.name" as keyof OrderAttributes, Ops.like(searchValue ?? "", "i")),
      orWhere("soldTo.externalKeys.code" as keyof OrderAttributes, Ops.like(searchValue ?? "", "i")),
      orWhere("soldTo.name" as keyof OrderAttributes, Ops.like(searchValue ?? "", "i")),
      andAllWhere("type.code", Ops.eq(typeCode)),
      andAllWhere("company.id", Ops.eq(companyId)),
      // andAllWhere("loadDateTime", Ops.btwnDates(date.start, date.end)),
      // andAllWhere("trip.dateHourEnd", Ops.not(null)),
      // andAllWhere("subRoute", Ops.eq(null)),
      // andAllWhere("operationType", Ops.not(4)),
      andAllWhere("statusId", Ops.eq(1)),
      //andAllWhere("statusERP", Ops.eq(0)),
    ];
  
    if (documentClassId && documentClassId.length > 0) {
      queryParts.push(andAllWhere("class.code", Ops.in(...documentClassId)));
    }

    if (salesOffice && salesOffice.length > 0) {
      queryParts.push(andAllWhere("salesOffice.id", Ops.in(...salesOffice)));
    }
    
    if (route){
      queryParts.push(andAllWhere("routes.id", Ops.in(...[route])));
    }

    if (subRoutes && subRoutes.length > 0) {
      queryParts.push(andAllWhere("subRoute.id", Ops.in(...subRoutes)));
    }else{
      // queryParts.push(andAllWhere("subRoute", Ops.not(null)));
    }

    if(businessPartners && businessPartners.length > 0){
      queryParts.push(andAllWhere("shipTo.id", Ops.in(...businessPartners)));
    }
  
    const query = buildQuery<any>(
      ...queryParts,
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
  
    let queryParams = '';
  
    if (salesOfficeGroup) {
      queryParams += `&salesOfficeGroupId=${salesOfficeGroup}`;
    }

    if(routeArea){
      queryParams += `routeAreaId=${routeArea}`;
    }

    if (routeGroup) {
      queryParams += `&subRouteGroupId=${routeGroup}`;
    }

    const filter = {
      date: {
        start: date.start,
        end: date.end
      },
      ...query 
    };

    const API_URL = `${loadUrl}api/${moduleName}/countByLoadDate?filter=${encodeURIComponent(JSON.stringify(filter))}${queryParams}`;
    const headers = new HttpHeaders({ 'x-ave-ctx': 'skip-loading' });
    return this.http.get<any>(API_URL, { headers });
  }


  findOrdersWithRouteAndTripForOnlyTrip(
    typeCode: string = 'DT003',
    tripId: number,
    options?: IPagination<OrderAttributes>
  ): Observable<IPaginationResultFrequencies<any>> {
    const queryParts: any[] = [
      where("serial" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("shipTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.externalKeys.code" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      orWhere("soldTo.name" as keyof OrderAttributes, Ops.like(options.search ?? "", "i")),
      andAllWhere("type.code", Ops.eq(typeCode)),
      andAllWhere("trip.id", Ops.eq(tripId)),
      andAllWhere("trip", Ops.not(null)),
      andAllWhere("statusId", Ops.eq(1)),
    ];
    const query = buildQuery<any>(
      ...queryParts,
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
  
    let queryParams = '';

    const API_URL = `${loadUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}${queryParams}`;
    return this.http.get<IPaginationResultFrequencies<OrderAttributes>>(API_URL);
  }

  findPendingOrdersByBP(
    typeCode: string = 'DT003',
    companyId: number,
    date?: {start: string, end: string},
    soldToId?: number,
    shipToId?: number,
    options?: IPagination<OrderAttributes>,
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {
    const queryParts:any[] = [
      where("type.code", Ops.eq(typeCode)),
      andWhere("company.id", Ops.eq(companyId)),
      andWhere("statusId", Ops.eq(1)),
    ]

    if(shipToId){
      queryParts.push(andWhere("shipTo.id", Ops.eq(shipToId)))
    }
    if(soldToId){
      queryParts.push(andWhere("soldTo.id", Ops.eq(soldToId)))
    }

    const query = buildQuery<any>(
      ...queryParts,
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResultFrequencies<OrderAttributes>>(API_URL);
  }
}
